(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-xmj-menu-picker]';
  const TRIGGER_SELECTOR = 'a[href="#xmj-outlets"], [data-xmj-menu-picker-trigger], [data-xmj-menu-trigger]';
  const FOCUSABLE_SELECTOR = [
    'button:not([disabled])',
    'a[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const registry = window.XMJMenuPicker || {
    roots: new Set(),
    active: null,
    installed: false,
    bodyLock: null
  };
  window.XMJMenuPicker = registry;

  function focusableElements(dialog) {
    return Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
      return !element.hidden && element.getClientRects().length > 0;
    });
  }

  function lockBody() {
    if (registry.bodyLock) return;
    registry.bodyLock = {
      overflow: document.body.style.overflow,
      paddingRight: document.body.style.paddingRight
    };
    document.body.classList.add('xmj-menu-picker-open');
  }

  function unlockBody() {
    if (!registry.bodyLock) return;
    document.body.classList.remove('xmj-menu-picker-open');
    document.body.style.overflow = registry.bodyLock.overflow;
    document.body.style.paddingRight = registry.bodyLock.paddingRight;
    registry.bodyLock = null;
  }

  function waitForDrawerClosed(drawer) {
    const details = drawer && (drawer.mainDetailsToggle || drawer.querySelector('details'));
    if (!details || !details.hasAttribute('open')) return Promise.resolve();

    return new Promise((resolve) => {
      const deadline = performance.now() + 550;
      const check = () => {
        if (!details.hasAttribute('open')) {
          resolve();
          return;
        }
        if (performance.now() >= deadline) {
          // Crave's implementation normally removes open after 400 ms. This
          // is a last-resort recovery if a theme extension interrupts it.
          details.removeAttribute('open');
          resolve();
          return;
        }
        window.requestAnimationFrame(check);
      };
      check();
    });
  }

  async function closeOwningDrawer(trigger) {
    const drawer = trigger && trigger.closest('header-drawer');
    if (!drawer) return trigger;

    const details = drawer.mainDetailsToggle || drawer.querySelector('details');
    const summary = details && details.querySelector(':scope > summary');
    if (!details || !details.hasAttribute('open')) return summary || trigger;

    if (summary) summary.setAttribute('aria-expanded', 'false');
    if (typeof drawer.closeMenuDrawer === 'function') {
      drawer.closeMenuDrawer(new Event('xmj-menu-picker-close-drawer'), summary || trigger);
    } else {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    }

    await waitForDrawerClosed(drawer);
    return summary || trigger;
  }

  function restoreFocus(target) {
    if (!target || !target.isConnected) return;
    window.requestAnimationFrame(() => {
      if (target.isConnected && typeof target.focus === 'function') {
        target.focus({ preventScroll: true });
      }
    });
  }

  function finishPicker(dialog) {
    const active = registry.active && registry.active.dialog === dialog ? registry.active : null;
    if (!active) return;
    registry.active = null;
    unlockBody();
    restoreFocus(active.restoreTarget);
  }

  function closePicker(dialog) {
    if (!dialog) return;
    if (dialog.open) {
      dialog.close();
    } else {
      // A theme-editor section can be removed while its dialog is closed or
      // while drawer closing is still being awaited. Clean up that state too.
      finishPicker(dialog);
    }
  }

  async function openPicker(trigger, root) {
    const dialog = root && root.querySelector('[data-xmj-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    if (registry.active && registry.active.dialog === dialog) return;
    if (registry.active) closePicker(registry.active.dialog);

    const active = {
      dialog,
      root,
      restoreTarget: trigger || document.activeElement
    };
    registry.active = active;
    active.restoreTarget = await closeOwningDrawer(trigger || document.activeElement);

    if (registry.active !== active || !dialog.isConnected) return;
    if (!dialog.open) dialog.showModal();
    lockBody();

    window.requestAnimationFrame(() => {
      if (registry.active !== active) return;
      const [first] = focusableElements(dialog);
      if (first) first.focus();
    });
  }

  function installRoot(root) {
    if (!root || root.dataset.xmjInitialized === 'true') return;
    const dialog = root.querySelector('[data-xmj-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function') return;

    root.dataset.xmjInitialized = 'true';
    root.dataset.xmjReady = 'true';
    registry.roots.add(root);

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-xmj-close]')) {
        event.preventDefault();
        closePicker(dialog);
        return;
      }

      if (event.target === dialog) {
        const bounds = dialog.getBoundingClientRect();
        const inside = event.clientX >= bounds.left && event.clientX <= bounds.right
          && event.clientY >= bounds.top && event.clientY <= bounds.bottom;
        if (!inside) closePicker(dialog);
      }
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closePicker(dialog);
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePicker(dialog);
        return;
      }
      if (event.key !== 'Tab') return;

      const elements = focusableElements(dialog);
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    dialog.addEventListener('click', (event) => {
      const link = event.target.closest('.xmj-menu-picker__outlet-link[href]');
      if (link) closePicker(dialog);
    });

    dialog.addEventListener('close', () => {
      const active = registry.active && registry.active.dialog === dialog ? registry.active : null;
      if (!active) return;
      finishPicker(dialog);
    });
  }

  function rootForTrigger(trigger) {
    const readyRoot = Array.from(registry.roots).find((root) => root.isConnected);
    return readyRoot || document.querySelector(ROOT_SELECTOR);
  }

  function handleTriggerClick(event) {
    const trigger = event.target.closest && event.target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;
    const root = rootForTrigger(trigger);
    if (!root || root.dataset.xmjReady !== 'true') return;
    event.preventDefault();
    openPicker(trigger, root);
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(installRoot);
  }

  function sectionTarget(event) {
    const sectionId = event.detail && event.detail.sectionId;
    if (!sectionId) return event.target;
    return document.querySelector(`[data-section-id="${CSS.escape(sectionId)}"]`) || event.target;
  }

  function installGlobalListeners() {
    if (registry.installed) return;
    registry.installed = true;
    document.addEventListener('click', handleTriggerClick);
    ['shopify:section:select', 'shopify:block:select'].forEach((eventName) => {
      document.addEventListener(eventName, (event) => {
        const target = sectionTarget(event);
        const root = target?.matches?.(ROOT_SELECTOR) ? target : target?.querySelector?.(ROOT_SELECTOR);
        if (root) { installRoot(root); openPicker(null, root); }
      });
    });
    document.addEventListener('shopify:section:load', (event) => {
      const target = sectionTarget(event);
      if (target && target.matches && target.matches(ROOT_SELECTOR)) installRoot(target);
      else if (target && target.querySelectorAll) target.querySelectorAll(ROOT_SELECTOR).forEach(installRoot);
    });
    document.addEventListener('shopify:section:unload', (event) => {
      const target = sectionTarget(event);
      const activeRoot = registry.active && registry.active.root;
      if (activeRoot && (activeRoot === target || !activeRoot.isConnected)) closePicker(registry.active.dialog);
      registry.roots.forEach((root) => {
        if (root === target || !root.isConnected) registry.roots.delete(root);
      });
    });
  }

  installGlobalListeners();
  initAll();
  document.addEventListener('DOMContentLoaded', initAll, { once: true });
})();
